import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { 
  ArrowLeft, BookOpen, FileText, Key, Calendar, GraduationCap, 
  CheckCircle, ExternalLink, HelpCircle, Edit2, Save, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestDetail() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const response = await axios.get(`${API}/tests/${testId}`);
        setTest(response.data);
        setEditedAnswers(response.data.answers || {});
      } catch (error) {
        toast.error("Failed to load test details");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId, navigate]);

  const handleAnswerChange = (questionNum, value) => {
    const sanitized = value.toUpperCase().replace(/[^A-E]/g, '').slice(0, 1);
    setEditedAnswers(prev => ({
      ...prev,
      [questionNum]: sanitized
    }));
  };

  const handleSaveAnswers = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/tests/${testId}/answers`, {
        answers: editedAnswers
      });
      toast.success("Answer key updated successfully!");
      setTest(prev => ({ ...prev, answers: editedAnswers }));
      setEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update answers");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedAnswers(test.answers || {});
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center">
        <p className="text-gray-500">Test not found</p>
      </div>
    );
  }

  const answers = test.answers || {};
  const concepts = test.concepts || {};
  const questions = test.questions || {};
  const sortedQuestions = Object.keys(answers)
    .map(Number)
    .sort((a, b) => a - b);

  // Group by concept for summary
  const conceptCounts = {};
  Object.values(concepts).forEach(concept => {
    conceptCounts[concept] = (conceptCounts[concept] || 0) + 1;
  });

  const hasQuestions = Object.keys(questions).length > 0;

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {test.name}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <GraduationCap className="w-4 h-4" />
                  {test.grade_level}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {test.year}
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {test.total_questions} Questions
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* PDF Links */}
        <div className="flex gap-4 mb-8">
          {test.test_sheet_url && (
            <a 
              href={`${BACKEND_URL}${test.test_sheet_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FileText className="w-5 h-5" />
              View Questions PDF
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {test.answer_key_url && (
            <a 
              href={`${BACKEND_URL}${test.answer_key_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              <Key className="w-5 h-5" />
              View Answer Key PDF
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Concept Summary */}
        {Object.keys(conceptCounts).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Topics Covered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(conceptCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([concept, count]) => (
                      <Badge 
                        key={concept} 
                        variant="secondary"
                        className="px-3 py-1 text-sm"
                      >
                        {concept}: {count} questions
                      </Badge>
                    ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Questions and Answers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <HelpCircle className="w-5 h-5 text-purple-600" />
                    {hasQuestions ? "Questions & Answers" : "Answer Key"}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {hasQuestions 
                      ? `${sortedQuestions.length} questions extracted from the PDF`
                      : `${sortedQuestions.length} answers extracted - questions not available`
                    }
                  </p>
                </div>
                {/* Edit Button */}
                {!editMode ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Answers
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCancelEdit}
                      className="gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveAnswers}
                      disabled={saving}
                      className="gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                )}
              </div>
              {editMode && (
                <p className="text-sm text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                  Edit mode: Click on any answer to change it. Only letters A-E are allowed.
                </p>
              )}
            </CardHeader>
            <CardContent>
              {hasQuestions ? (
                /* Full questions view with accordion */
                <Accordion type="multiple" className="space-y-2">
                  {sortedQuestions.map((qNum) => {
                    const question = questions[qNum] || questions[String(qNum)] || {};
                    const correctAnswer = editMode 
                      ? (editedAnswers[qNum] || editedAnswers[String(qNum)] || "?")
                      : (answers[qNum] || answers[String(qNum)] || "?");
                    const concept = concepts[qNum] || concepts[String(qNum)] || "General";
                    const choices = question.choices || {};
                    
                    return (
                      <AccordionItem 
                        key={qNum} 
                        value={`q-${qNum}`}
                        className="border rounded-lg px-4 bg-white"
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-4 text-left">
                            <span className="flex-shrink-0 w-10 h-10 bg-purple-100 text-purple-700 rounded-lg flex items-center justify-center font-bold">
                              {qNum}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 line-clamp-2">
                                {question.text || `Question ${qNum}`}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {concept}
                                </Badge>
                                {editMode ? (
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <span className="text-sm text-gray-600">Answer:</span>
                                    <Input
                                      value={editedAnswers[String(qNum)] || ""}
                                      onChange={(e) => handleAnswerChange(String(qNum), e.target.value)}
                                      className="w-12 h-7 text-center font-bold text-green-600 uppercase"
                                      maxLength={1}
                                    />
                                  </div>
                                ) : (
                                  <span className="text-sm text-green-600 font-semibold">
                                    Answer: {correctAnswer}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                          <div className="pl-14 space-y-3">
                            {/* Full question text */}
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-gray-800">{question.text || "Question text not available"}</p>
                            </div>
                            
                            {/* Answer choices */}
                            {Object.keys(choices).length > 0 && (
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-600">Answer Choices:</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {Object.entries(choices).sort().map(([letter, text]) => (
                                    <div 
                                      key={letter}
                                      className={`flex items-start gap-2 p-2 rounded-lg ${
                                        letter === correctAnswer 
                                          ? 'bg-green-100 border-2 border-green-500' 
                                          : 'bg-gray-50 border border-gray-200'
                                      }`}
                                    >
                                      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                                        letter === correctAnswer
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-300 text-gray-700'
                                      }`}>
                                        {letter}
                                      </span>
                                      <span className={`text-sm ${letter === correctAnswer ? 'font-medium text-green-800' : 'text-gray-700'}`}>
                                        {text}
                                      </span>
                                      {letter === correctAnswer && (
                                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 ml-auto" />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                /* Simple answer grid when questions aren't available */
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                    {sortedQuestions.map((qNum) => (
                      <div 
                        key={qNum}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                      >
                        <span className="text-sm font-medium text-gray-500 w-8">Q{qNum}</span>
                        {editMode ? (
                          <Input
                            value={editedAnswers[String(qNum)] || ""}
                            onChange={(e) => handleAnswerChange(String(qNum), e.target.value)}
                            className="w-12 h-8 text-center font-bold text-green-600 uppercase text-xl"
                            maxLength={1}
                          />
                        ) : (
                          <span className="text-xl font-bold text-green-600 font-mono">
                            {answers[qNum] || answers[String(qNum)] || "?"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 text-center">
                    {editMode 
                      ? "Click Save Changes when done editing." 
                      : "Question text not available for this test. Upload a new test to extract full questions."
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
